import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import toast from "react-hot-toast";
import {
  X, Send, Loader2, Bot, User, MessageCircle,
  Ticket, ChevronDown, Clock, CheckCircle, AlertTriangle,
  HelpCircle, Sparkles, ArrowLeft
} from "lucide-react";
import sanitizeText from "../utils/complianceFilter";

const CATEGORIES = [
  { id: "account", label: "Account", icon: User },
  { id: "payments", label: "Payments & Deposits", icon: "SC" },
  { id: "withdrawal", label: "Withdrawals", icon: "SC" },
  { id: "games", label: "Games", icon: "G" },
  { id: "verification", label: "Verification / KYC", icon: "V" },
  { id: "bonus", label: "Bonuses & Promos", icon: "B" },
  { id: "technical", label: "Technical Issue", icon: "T" },
  { id: "other", label: "Other", icon: "?" },
];

const STATUS_COLORS = {
  open: "bg-amber-500",
  in_progress: "bg-blue-500",
  resolved: "bg-emerald-500",
  closed: "bg-zinc-500",
};

const STATUS_LABELS = {
  open: "Open",
  in_progress: "In Progress",
  resolved: "Resolved",
  closed: "Closed",
};

const SupportPanel = ({ isOpen, onClose }) => {
  const { user, token } = useAuth();
  const [view, setView] = useState("main"); // main, chat, ticket-form, my-tickets, ticket-detail
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [myTickets, setMyTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketReply, setTicketReply] = useState("");
  const [ticketForm, setTicketForm] = useState({ category: "", subject: "", message: "", priority: "normal" });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && view === "chat") {
      const savedSession = localStorage.getItem("support_session_id");
      if (savedSession) {
        setSessionId(savedSession);
        loadChatHistory(savedSession);
      }
    }
  }, [isOpen, view]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (view === "chat" && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [view]);

  if (!isOpen) return null;

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const loadChatHistory = async (sid) => {
    try {
      const res = await axios.get(`${API}/support/history/${sid}`, { headers });
      if (res.data.messages?.length > 0) setMessages(res.data.messages);
    } catch (e) { /* ignore */ }
  };

  const sendMessage = async (text = inputValue) => {
    if (!text.trim() || isLoading) return;
    const userMsg = { role: "user", content: text, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInputValue("");
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/support/chat`, { message: text, session_id: sessionId }, { headers });
      if (res.data.session_id !== sessionId) {
        setSessionId(res.data.session_id);
        localStorage.setItem("support_session_id", res.data.session_id);
      }
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response, timestamp: new Date().toISOString(), escalated: res.data.escalated }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, I'm having trouble right now. Please try again or submit a support ticket.", timestamp: new Date().toISOString() }]);
    } finally { setIsLoading(false); }
  };

  const loadMyTickets = async () => {
    try {
      const res = await axios.get(`${API}/support/tickets/mine`, { headers });
      setMyTickets(res.data.tickets || []);
    } catch (e) { toast.error("Failed to load tickets"); }
  };

  const submitTicket = async () => {
    if (!ticketForm.category || !ticketForm.subject || !ticketForm.message) {
      toast.error("Please fill in all fields");
      return;
    }
    try {
      const res = await axios.post(`${API}/support/tickets`, ticketForm, { headers });
      toast.success(res.data.message);
      setTicketForm({ category: "", subject: "", message: "", priority: "normal" });
      setView("my-tickets");
      loadMyTickets();
    } catch (e) { toast.error("Failed to submit ticket"); }
  };

  const submitTicketReply = async () => {
    if (!ticketReply.trim() || !selectedTicket) return;
    try {
      await axios.post(`${API}/support/tickets/${selectedTicket.ticket_id}/reply`, { message: ticketReply }, { headers });
      setTicketReply("");
      // Reload ticket
      const res = await axios.get(`${API}/support/tickets/${selectedTicket.ticket_id}`, { headers });
      setSelectedTicket(res.data);
    } catch (e) { toast.error("Failed to send reply"); }
  };

  const goBack = () => {
    if (view === "ticket-detail") { setView("my-tickets"); setSelectedTicket(null); }
    else setView("main");
  };

  // Quick answers for AI chat
  const quickAnswers = [
    { text: "How do I buy coins?", icon: "SC" },
    { text: "How do I redeem prizes?", icon: "G" },
    { text: "How do I verify my account?", icon: "V" },
    { text: "What bonuses are available?", icon: "B" },
  ];

  return (
    <div className="fixed inset-0 bg-black/70 z-[70] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-[#0a0e14] rounded-2xl border border-white/10 w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="support-panel"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-600/30 to-orange-600/20 border-b border-white/10 px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            {view !== "main" && (
              <button onClick={goBack} className="p-1 hover:bg-white/10 rounded-lg" data-testid="support-back-btn">
                <ArrowLeft className="w-4 h-4 text-white" />
              </button>
            )}
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
              <HelpCircle className="w-4 h-4 text-black" />
            </div>
            <div>
              <h3 className="text-white font-bold text-sm">
                {view === "main" ? "Support Center" : view === "chat" ? "AI Support Agent" : view === "ticket-form" ? "Submit a Ticket" : view === "my-tickets" ? "My Tickets" : `Ticket #${selectedTicket?.ticket_id}`}
              </h3>
              {view === "chat" && (
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                  <span className="text-emerald-400 text-[10px]">Online</span>
                </div>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg" data-testid="support-close-btn">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        {/* ============ MAIN VIEW ============ */}
        {view === "main" && (
          <div className="p-5 space-y-3 overflow-y-auto">
            <p className="text-zinc-400 text-sm">How can we help you today?</p>

            {/* AI Chat option */}
            <button
              onClick={() => setView("chat")}
              className="w-full flex items-center gap-4 p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl hover:border-amber-400/50 transition-all group"
              data-testid="support-ai-chat-btn"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0">
                <Bot className="w-6 h-6 text-black" />
              </div>
              <div className="text-left flex-1">
                <h4 className="text-white font-bold text-sm">Chat with AI Support</h4>
                <p className="text-zinc-400 text-xs">Get instant answers to common questions</p>
              </div>
              <Sparkles className="w-5 h-5 text-amber-400 group-hover:animate-pulse" />
            </button>

            {/* Submit ticket */}
            <button
              onClick={() => setView("ticket-form")}
              className="w-full flex items-center gap-4 p-4 bg-white/5 border border-white/10 rounded-xl hover:border-white/20 transition-all group"
              data-testid="support-submit-ticket-btn"
            >
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <Ticket className="w-6 h-6 text-emerald-400" />
              </div>
              <div className="text-left flex-1">
                <h4 className="text-white font-bold text-sm">Submit a Ticket</h4>
                <p className="text-zinc-400 text-xs">Get help from our support team</p>
              </div>
              <Send className="w-5 h-5 text-zinc-500 group-hover:text-white" />
            </button>

            {/* View my tickets */}
            <button
              onClick={() => { setView("my-tickets"); loadMyTickets(); }}
              className="w-full flex items-center gap-4 p-4 bg-white/5 border border-white/10 rounded-xl hover:border-white/20 transition-all group"
              data-testid="support-my-tickets-btn"
            >
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-6 h-6 text-blue-400" />
              </div>
              <div className="text-left flex-1">
                <h4 className="text-white font-bold text-sm">My Tickets</h4>
                <p className="text-zinc-400 text-xs">View status of your support requests</p>
              </div>
              <Clock className="w-5 h-5 text-zinc-500 group-hover:text-white" />
            </button>
          </div>
        )}

        {/* ============ AI CHAT VIEW ============ */}
        {view === "chat" && (
          <>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-zinc-400 text-sm mb-3">Ask me anything about JackRoyals!</p>
                  <div className="grid grid-cols-2 gap-2">
                    {quickAnswers.map((qa, i) => (
                      <button key={i} onClick={() => sendMessage(qa.text)} className="text-left p-2.5 bg-white/5 hover:bg-white/10 rounded-lg border border-white/5 hover:border-amber-500/30 transition-all">
                        <span className="text-white text-xs">{qa.text}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((msg, i) => (
                <div key={i} className={`flex gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  {msg.role === "assistant" && (
                    <div className="w-7 h-7 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0 mt-1">
                      <Bot className="w-3.5 h-3.5 text-black" />
                    </div>
                  )}
                  <div className={`max-w-[80%] p-3 rounded-2xl text-sm ${msg.role === "user" ? "bg-amber-500 text-black rounded-br-sm" : "bg-white/10 text-white rounded-bl-sm"}`}>
                    <p className="whitespace-pre-wrap">{msg.role === "assistant" ? sanitizeText(msg.content) : msg.content}</p>
                    {msg.escalated && (
                      <div className="mt-2 flex items-center gap-1.5 text-amber-400 text-xs">
                        <AlertTriangle className="w-3 h-3" />
                        <span>This issue may need human review</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex gap-2">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
                    <Bot className="w-3.5 h-3.5 text-black" />
                  </div>
                  <div className="bg-white/10 rounded-2xl rounded-bl-sm p-3 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />
                    <span className="text-zinc-400 text-sm">Thinking...</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            <div className="border-t border-white/10 p-3 bg-black/30 flex-shrink-0">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  placeholder="Type your message..."
                  className="flex-1 px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-amber-500/50"
                  disabled={isLoading}
                  data-testid="support-chat-input"
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={!inputValue.trim() || isLoading}
                  className="px-4 py-2.5 bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-700 disabled:text-zinc-500 text-black rounded-xl font-bold text-sm transition-colors"
                  data-testid="support-chat-send"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <button
                onClick={() => setView("ticket-form")}
                className="w-full mt-2 text-xs text-amber-400 hover:text-amber-300 text-center"
              >
                Need more help? Submit a support ticket
              </button>
            </div>
          </>
        )}

        {/* ============ TICKET FORM VIEW ============ */}
        {view === "ticket-form" && (
          <div className="p-4 space-y-4 overflow-y-auto">
            <div>
              <label className="text-xs font-bold text-zinc-400 mb-1.5 block">Category *</label>
              <div className="grid grid-cols-2 gap-2">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setTicketForm(f => ({ ...f, category: cat.id }))}
                    data-testid={`ticket-category-${cat.id}`}
                    className={`p-2.5 rounded-lg text-xs font-bold text-left transition-all ${
                      ticketForm.category === cat.id
                        ? "bg-amber-500 text-black border border-amber-400"
                        : "bg-white/5 text-zinc-300 border border-white/10 hover:border-white/20"
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-zinc-400 mb-1.5 block">Subject *</label>
              <input
                value={ticketForm.subject}
                onChange={(e) => setTicketForm(f => ({ ...f, subject: e.target.value }))}
                placeholder="Brief description of your issue"
                className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-amber-500/50"
                data-testid="ticket-subject-input"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-zinc-400 mb-1.5 block">Priority</label>
              <div className="flex gap-2">
                {["low", "normal", "high", "urgent"].map(p => (
                  <button
                    key={p}
                    onClick={() => setTicketForm(f => ({ ...f, priority: p }))}
                    data-testid={`ticket-priority-${p}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold capitalize transition-all ${
                      ticketForm.priority === p
                        ? p === "urgent" ? "bg-red-500 text-white" : p === "high" ? "bg-orange-500 text-black" : p === "normal" ? "bg-blue-500 text-white" : "bg-zinc-600 text-white"
                        : "bg-white/5 text-zinc-400 border border-white/10"
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-zinc-400 mb-1.5 block">Message *</label>
              <textarea
                value={ticketForm.message}
                onChange={(e) => setTicketForm(f => ({ ...f, message: e.target.value }))}
                placeholder="Describe your issue in detail..."
                rows={4}
                className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-amber-500/50 resize-none"
                data-testid="ticket-message-input"
              />
            </div>
            <button
              onClick={submitTicket}
              disabled={!ticketForm.category || !ticketForm.subject || !ticketForm.message}
              className="w-full py-3 bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-700 disabled:text-zinc-500 text-black font-bold rounded-xl transition-colors"
              data-testid="ticket-submit-btn"
            >
              Submit Ticket
            </button>
          </div>
        )}

        {/* ============ MY TICKETS VIEW ============ */}
        {view === "my-tickets" && (
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {myTickets.length === 0 ? (
              <div className="text-center py-10">
                <Ticket className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-500 text-sm">No tickets yet</p>
                <button onClick={() => setView("ticket-form")} className="mt-3 text-amber-400 text-sm hover:text-amber-300">
                  Submit your first ticket
                </button>
              </div>
            ) : (
              myTickets.map(t => (
                <button
                  key={t.ticket_id}
                  onClick={() => { setSelectedTicket(t); setView("ticket-detail"); }}
                  className="w-full text-left p-3 bg-white/5 border border-white/10 rounded-xl hover:border-white/20 transition-all"
                  data-testid={`ticket-item-${t.ticket_id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-white text-sm font-bold">#{t.ticket_id}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold text-white ${STATUS_COLORS[t.status]}`}>
                      {STATUS_LABELS[t.status]}
                    </span>
                  </div>
                  <p className="text-white text-xs truncate">{t.subject}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-zinc-500 text-[10px] capitalize">{t.category}</span>
                    <span className="text-zinc-600 text-[10px]">|</span>
                    <span className="text-zinc-500 text-[10px]">{new Date(t.created_at).toLocaleDateString()}</span>
                  </div>
                </button>
              ))
            )}
          </div>
        )}

        {/* ============ TICKET DETAIL VIEW ============ */}
        {view === "ticket-detail" && selectedTicket && (
          <>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-500 text-xs capitalize">{selectedTicket.category} | {selectedTicket.priority} priority</p>
                  <h4 className="text-white font-bold text-sm">{selectedTicket.subject}</h4>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold text-white ${STATUS_COLORS[selectedTicket.status]}`}>
                  {STATUS_LABELS[selectedTicket.status]}
                </span>
              </div>
              <div className="border-t border-white/5 pt-3 space-y-3">
                {selectedTicket.messages?.map((msg, i) => (
                  <div key={i} className={`flex gap-2 ${msg.sender === "user" ? "justify-end" : "justify-start"}`}>
                    {msg.sender === "admin" && (
                      <div className="w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 mt-1">
                        <CheckCircle className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}
                    <div className={`max-w-[80%] p-3 rounded-2xl text-sm ${msg.sender === "user" ? "bg-amber-500/20 text-white border border-amber-500/20 rounded-br-sm" : "bg-emerald-500/10 text-white border border-emerald-500/20 rounded-bl-sm"}`}>
                      <p className="text-[10px] font-bold mb-1 text-zinc-400">{msg.sender === "admin" ? "Support Team" : msg.sender_name}</p>
                      <p className="whitespace-pre-wrap">{sanitizeText(msg.content)}</p>
                      <p className="text-[9px] text-zinc-500 mt-1">{new Date(msg.timestamp).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {selectedTicket.status !== "closed" && (
              <div className="border-t border-white/10 p-3 bg-black/30 flex-shrink-0">
                <div className="flex gap-2">
                  <input
                    value={ticketReply}
                    onChange={(e) => setTicketReply(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitTicketReply(); } }}
                    placeholder="Add a reply..."
                    className="flex-1 px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-amber-500/50"
                    data-testid="ticket-reply-input"
                  />
                  <button
                    onClick={submitTicketReply}
                    disabled={!ticketReply.trim()}
                    className="px-4 py-2.5 bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-700 text-black rounded-xl font-bold text-sm"
                    data-testid="ticket-reply-send"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SupportPanel;
